import { useEffect, useRef, useState } from "react";
import cx from "classnames";
import { useStripe } from "@stripe/react-stripe-js";
import {
  faHouse,
  faNote,
  faTruckFast,
  faUser,
  faWalking,
} from "@fortawesome/pro-regular-svg-icons";

import { PaymentMethodOption, KlarnaPaymentMethodPlaceholder } from "common/types";
import { DeliveryGroupKind, ClientCartDetail } from "common/types/carts";
import { CartPriceSummary } from "common/types/priceSummary";
import { deepCombineErrors } from "common/utils/errors";
import { toDollars } from "common/utils/formatting";
import { useSearchState } from "common/router/hooks";
import Icon from "common/components/Icon";

import {
  walletDisplayText,
  getWalletPaymentItems,
  resolveSupportedWalletByPriority,
  isPaymentOptionChargeable,
  UNCHARGEABLE_PAYMENT_METHOD_MESSAGE,
} from "client/payments/utils";
import { Paths, urls } from "client/routes";
import Modal from "client/common/components/deprecated-modals/Modal";
import { useCartUpdateMutation } from "client/carts/hooks";
import PaymentMethodSelectPanel from "client/payments/components/PaymentMethodSelectPanel";
import { useCheckoutMutation } from "client/checkout/hooks";
import { IClient } from "client/accounts/types";
import Box from "client/common/components/layouts/Box";
import Address from "client/common/components/content/Address";
import { Clinic } from "client/clinics/types";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";
import TextArea from "client/common/components/inputs/TextArea";
import TextButton from "client/common/components/buttons/TextButton";
import SelectPanel from "client/common/components/inputs/SelectPanel";
import SelectButton from "client/common/components/buttons/SelectButton";
import Accordion from "client/common/components/interactive/Accordion";
// TODO: Don't import these from the products app once we build this out for real
import { ZOETIS_CONTENT } from "client/products/blocks/constants";
import MarkdownText from "client/products/blocks/components/helper/MarkdownText";
import { useFeatures } from "client/clinics/hooks";
import Alert from "client/common/components/content/Alert";
import Checkbox from "client/common/components/inputs/Checkbox";
import CardPaymentDeclineAlert from "client/common/components/content/CardPaymentDeclineAlert";
import { usePaymentCreateDrawer } from "client/payments/stackables";
import {
  useCreatePaymentMethodMutation,
  useWalletPaymentMethodRequest,
} from "client/payments/hooks";
import { sendAddPaymentInfo, useSendPostHogUpdatePaymentMethod } from "client/analytics/utils";

import WalletCheckoutDialog from "../WalletCheckoutDialog";
import { CheckoutStepProps, KlarnaRedirectParams } from "../../types";
import StepLayout from "../CheckoutStepLayout";
import PetcareRewardsIcon from "../icons/PetcareRewardsIcon";
import KlarnaCheckoutDialog from "../KlarnaCheckoutDialog";
import CheckoutSummary from "../CheckoutSummary";

import styles from "../../styles/PaymentMethodCheckoutStep.module.less";

type NoteModalProps = {
  isVisible: boolean;
  onClose: () => void;
  cart: ClientCartDetail;
};

const NoteModal = ({ isVisible, onClose, cart }: NoteModalProps) => {
  const {
    mutate: updateCart,
    isPending: isCartUpdating,
    error: cartError,
  } = useCartUpdateMutation(cart.id, {
    onSuccess: onClose,
  });

  const [note, setNote] = useState(cart.note);

  return (
    <Modal visible={isVisible} onClose={onClose}>
      <ModalLayout>
        <ModalLayout.Header
          title="Add a Note"
          description="Add some additional context or information for this order."
          onClose={onClose}
        />
        <ModalLayout.Form
          canSubmit={true}
          error={cartError}
          isLoading={isCartUpdating}
          submitText={note.length ? "Add Note" : "Clear Note"}
          onSubmit={() => updateCart({ body: { note }, cartUpdate: { note } })}
        >
          <TextArea label="Note to clinic" value={note} onChange={setNote} />
        </ModalLayout.Form>
      </ModalLayout>
    </Modal>
  );
};

type ReviewInfoProps = {
  clinic: Clinic;
  cart: ClientCartDetail;
  client: IClient;
};

const ReviewInfo = (props: ReviewInfoProps) => {
  const { clinic, cart, client } = props;

  const [isNoteModalVisible, setIsNoteModalVisible] = useState(false);

  const infos = [
    {
      label: "Contact",
      icon: faUser,
      value: (
        <div>
          <div>{client.primary_contact_number?.number_display}</div>
          <div className={styles.secondary}>{client.primary_contact_email?.email}</div>
        </div>
      ),
    },
    {
      label: "Ship To",
      icon: faHouse,
      value: cart.shipping_address ? (
        <Address
          address={cart.shipping_address}
          styles={{ street: styles.secondary, address: styles.secondary }}
        />
      ) : (
        "-"
      ),
      visible: !cart.is_all_pickup,
    },
    {
      label: "Shipping Method",
      icon: faTruckFast,
      value: cart.delivery_groups
        .filter((group) => group.kind !== DeliveryGroupKind.Pickup)
        .map((group) => (
          <div key={group.key}>
            <div>{group.selected_delivery_option?.title}</div>
            <div className={styles.secondary}>{group.selected_delivery_option?.description}</div>
          </div>
        )),
      visible: !cart.is_all_pickup,
    },
    {
      label: "Pickup from",
      icon: faWalking,
      value: cart.pickup_address ? (
        <Address
          address={cart.pickup_address}
          styles={{ street: styles.secondary, address: styles.secondary }}
        />
      ) : (
        clinic.display_name
      ),
      visible: cart.has_pickup,
    },
    {
      label: "Note to Clinic",
      icon: faNote,
      value: cart.note ? (
        <div>
          <div>{cart.note}</div>
          <TextButton
            type="button"
            className={styles.edit}
            onClick={() => setIsNoteModalVisible(true)}
          >
            Edit note
          </TextButton>
        </div>
      ) : (
        <TextButton icon="right" type="button" onClick={() => setIsNoteModalVisible(true)}>
          Add note
        </TextButton>
      ),
    },
  ].filter(({ visible = true }) => visible);

  return (
    <Box className={styles.review}>
      {infos.map((info) => (
        <div className={styles.info} key={info.label}>
          <Icon icon={info.icon} className={styles.icon} fixedWidth={true} />
          <div className={styles.infoContent}>
            <div className={styles.label}>{info.label}</div>
            <div className={styles.value}>{info.value}</div>
          </div>
        </div>
      ))}
      <NoteModal
        cart={cart}
        onClose={() => setIsNoteModalVisible(false)}
        isVisible={isNoteModalVisible}
      />
    </Box>
  );
};

const ZoetisPetcareRewardsSection = () => {
  const [isEnabled, setIsEnabled] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const options = [
    {
      label: "Yes please, enroll for free",
      description:
        "You will earn points on today's purchase to help pay for any product or service at our practice.",
      value: true,
      icon: (
        <PetcareRewardsIcon
          classNames={{
            icon: styles.logo,
            petcare: styles.petcare,
            rewards: styles.rewards,
          }}
        />
      ),
    },
    {
      label: "No thank you, not at this time",
      description: "You will not earn points on today's purchase.",
      value: false,
    },
  ];

  return (
    <StepLayout.Section>
      <StepLayout.Header>
        <StepLayout.Title>Enroll in Zoetis Petcare Rewards</StepLayout.Title>
        <StepLayout.Description>
          For every 10 points you earn with eligible product purchases, you&apos;ll get $1 in
          Rewards to spend on future vet visits.{" "}
          <TextButton onClick={() => setIsModalOpen(true)} icon="right" hasUnderline={true}>
            Learn More
          </TextButton>
        </StepLayout.Description>
      </StepLayout.Header>
      <div className={cx(styles.zoetis, !isEnabled && styles.disabled)}>
        <SelectPanel
          getKey={(option) => option?.value ?? ""}
          options={options}
          renderOption={(option, { isActive }) => (
            <SelectButton.DefinitionContent
              align="start"
              status={option.icon}
              isActive={isActive}
              label={option.label}
              description={option.description}
            />
          )}
          value={isEnabled ? options[0] : options[1]}
          onClick={(option) => setIsEnabled(option.value)}
        />
      </div>
      <Modal visible={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalLayout>
          <ModalLayout.Header
            title="Zoetis Petcare Rewards FAQs"
            onClose={() => setIsModalOpen(false)}
          />
          <ModalLayout.Content>
            <Accordion.Single
              // TODO: Don't import these from the products app once we build this out for real
              items={ZOETIS_CONTENT.faqs.map((faq) => ({
                key: faq.question,
                label: <MarkdownText value={faq.question} />,
                content: (
                  <div className={styles.modal}>
                    <MarkdownText.Block value={faq.answer} />
                  </div>
                ),
              }))}
            />
          </ModalLayout.Content>
        </ModalLayout>
      </Modal>
    </StepLayout.Section>
  );
};

type PaymentMethodCheckoutStepProps = CheckoutStepProps;

type SpecialCheckoutFlow = "klarna" | "wallet";

const PaymentMethodCheckoutStep = ({
  cart,
  clinic,
  client,
  onPrevious,
  onNext,
  isValid,
  isLoading,
  taxError,
  taxAmount,
}: PaymentMethodCheckoutStepProps) => {
  const stripe = useStripe();
  const features = useFeatures();
  const { open: openPaymentCreateDrawer } = usePaymentCreateDrawer();
  const [isAutoshipConfirmed, setIsAutoshipConfirmed] = useState(false);
  const sendPostHogUpdatePaymentMethod = useSendPostHogUpdatePaymentMethod();
  // Tracks the payment_method id we last sent add_payment_info for, so the Save
  // Card path and the step-submit fallback don't double-fire for the same method.
  const addPaymentInfoFiredForIdRef = useRef<PaymentMethodOption["id"] | null>(null);

  const fireAddPaymentInfoOnce = (option: PaymentMethodOption) => {
    if (addPaymentInfoFiredForIdRef.current === option.id) {
      return;
    }
    sendAddPaymentInfo(cart, option);
    sendPostHogUpdatePaymentMethod(cart, option);
    addPaymentInfoFiredForIdRef.current = option.id;
  };
  // since the checkout payment page now supports klarna, read url params to determine what stage of checkout we're in
  const [search] = useSearchState<Paths["checkout"]["detail"]>({
    redirect_status: undefined,
  });
  const { redirect_status: klarnaRedirectStatus } = search;
  const klarnaSetupPaymentFailed = klarnaRedirectStatus === KlarnaRedirectParams.Failed;
  const klarnaSetupPaymentSucceeded =
    // redirect status indicating success in production
    klarnaRedirectStatus === KlarnaRedirectParams.Succeeded ||
    // redirect status indicating success in testing
    klarnaRedirectStatus === KlarnaRedirectParams.Pending;

  const [checkoutFlow, setCheckoutFlow] = useState<SpecialCheckoutFlow | undefined>(undefined);
  const [isRedirectingKlarnaFlow, setIsRedirectingKlarnaFlow] = useState(false);
  const [isFinishingWalletFlow, setIsFinishingWalletFlow] = useState(false);

  const {
    mutate: checkout,
    isPending: isCheckoutLoading,
    isSuccess: isCheckoutSuccess,
    error: checkoutError,
  } = useCheckoutMutation(cart, {
    onSuccess: onNext,
    onError: (error) => {
      if (error.data?.klarna_redirect_url) {
        // redirect client to provide payment information to setup a klarna payment
        window.location.href = error.data.klarna_redirect_url;
        setIsRedirectingKlarnaFlow(true);
        return;
      }
      if (!stripe || !error.data?.stripe_action_client_secret) {
        return;
      }
      stripe.handleCardAction(error.data?.stripe_action_client_secret).then((result) => {
        if (result.paymentIntent) {
          checkout({ is_klarna_checkout: false });
        }
      });
    },
  });

  const cardDeclineError = checkoutError?.data?.card_decline_info
    ? checkoutError.data.card_decline_info
    : null;

  const checkoutErrors = deepCombineErrors(checkoutError?.errors);
  const checkoutErrorMessage =
    checkoutErrors.length > 0 ? checkoutErrors[0] : checkoutError?.detail;

  const methods = client.payment_methods;

  const { mutate: updateCart, error: cartError } = useCartUpdateMutation(cart.id);

  const { mutateAsync: createWalletPaymentMethod, error: createWalletPaymentMethodError } =
    useCreatePaymentMethodMutation({
      onSuccess: (method) => {
        updateCart(
          {
            body: {
              payment_method_id: method.id,
            },
            cartUpdate: { payment_method: method },
          },
          {
            onSuccess: () => {
              checkout(
                { is_klarna_checkout: false },
                {
                  onError: () => {
                    setIsFinishingWalletFlow(false);
                    setCheckoutFlow(undefined);
                  },
                },
              );
            },
            onError: () => {
              setIsFinishingWalletFlow(false);
              setCheckoutFlow(undefined);
            },
          },
        );
      },
    });

  const formError = cartError || createWalletPaymentMethodError;

  const {
    request: walletPaymentRequest,
    supportedWallets,
    isLoading: isWalletPaymentRequestLoading,
  } = useWalletPaymentMethodRequest({
    label: cart.clinic.client_display_name,
    amount: cart.total,
    paymentItems: getWalletPaymentItems<CartPriceSummary>({
      ...cart,
      tax: null,
      is_tax_final: false,
    }),
    onCreate: async (id) => {
      await createWalletPaymentMethod({ payment_method: id });
      setIsFinishingWalletFlow(true);
    },
  });

  const walletType = resolveSupportedWalletByPriority(supportedWallets);

  // Since we're lazy loading the next page, show success as loading to avoid weirdness
  const isSubmitting = isCheckoutLoading || isCheckoutSuccess || isRedirectingKlarnaFlow;

  const total = taxAmount ? cart.total + taxAmount : cart.total;
  const klarnaPaymentOption: KlarnaPaymentMethodPlaceholder = {
    id: "__klarna__",
    wallet: "",
    is_klarna: true,
    //  prevent client surprises by rounding payments to next cent
    label: `4 interest free payments of ${toDollars(total / 4, { roundToNextCent: true })}`,
  };

  const selectedPaymentOption = (): PaymentMethodOption | undefined => {
    if (checkoutFlow === "klarna" || cart.payment_method?.is_klarna) {
      return klarnaPaymentOption;
    }

    if (checkoutFlow === "wallet" && walletType && !isFinishingWalletFlow) {
      return {
        id: "__wallet__",
        wallet: walletType,
        is_klarna: false,
      };
    }

    return cart.payment_method ?? undefined;
  };

  // either checkout with Klarna or with card depending on which payment method option was selected
  const handleSubmit = () => {
    const paymentOption = selectedPaymentOption();
    if (paymentOption) {
      fireAddPaymentInfoOnce(paymentOption);
    }

    if (checkoutFlow === "klarna") {
      checkout({ is_klarna_checkout: true });
      return;
    }
    if (checkoutFlow === "wallet" && walletType) {
      walletPaymentRequest?.show();
      return;
    }

    checkout({ is_klarna_checkout: false });
  };

  const handlePaymentMethodChange = (payment_method: PaymentMethodOption) => {
    if (isSubmitting) {
      return;
    }

    if (payment_method.id === "__klarna__") {
      setCheckoutFlow("klarna");
      return;
    }
    if (payment_method.id === "__wallet__") {
      setCheckoutFlow("wallet");
      return;
    }

    // otherwise this is a standard payment method selection
    setCheckoutFlow(undefined);
    updateCart({
      body: {
        payment_method_id: payment_method.id,
      },
      cartUpdate: { payment_method },
    });
    fireAddPaymentInfoOnce(payment_method);
  };

  const selectedPaymentOptionValue = selectedPaymentOption();
  const selectedPaymentIsUnchargeable =
    !!selectedPaymentOptionValue && !isPaymentOptionChargeable(selectedPaymentOptionValue);

  const isCheckoutDisabled =
    (!checkoutFlow && !isValid) ||
    (cart.is_autoship_confirmation_required && !isAutoshipConfirmed) ||
    selectedPaymentIsUnchargeable;

  const canPlaceOrderWithKlarna =
    klarnaSetupPaymentSucceeded && !isCheckoutLoading && !checkoutError;

  // if we reload the cart, let's make sure we restart the klarna flow context.
  useEffect(() => {
    if (cart.payment_method?.is_klarna && checkoutFlow !== "klarna") {
      setCheckoutFlow("klarna");
    }
  }, [cart.payment_method?.is_klarna, checkoutFlow]);

  // let's make sure that we complete the klarna flow if we are returning to the checkout page
  // from the klarna managed payment page.
  useEffect(() => {
    if (canPlaceOrderWithKlarna) {
      checkout({ is_klarna_checkout: true });
    }
  }, [canPlaceOrderWithKlarna, checkout, checkoutFlow]);

  const hasKlarnaDialog =
    klarnaSetupPaymentSucceeded && checkoutFlow === "klarna" && !checkoutError;
  const hasWalletDialog = isFinishingWalletFlow && checkoutFlow === "wallet" && !checkoutError;

  const getCheckoutDisabledHelp = () => {
    if (cart.warning_messages.length) {
      return "Please fix the issues with your cart items before proceeding.";
    }
    if (cart.is_autoship_confirmation_required && !isAutoshipConfirmed) {
      return "Please confirm that you've read the autoship information before proceeding.";
    }
    if (selectedPaymentIsUnchargeable) {
      return UNCHARGEABLE_PAYMENT_METHOD_MESSAGE;
    }
    return "Please select a payment method before proceeding.";
  };

  const subscriptionCartItems = cart.items.filter((cartItem) => !!cartItem.subscription);

  const renderCheckoutAlert = () => {
    if (cardDeclineError) {
      return <CardPaymentDeclineAlert declineError={cardDeclineError} />;
    }
    if (checkoutErrorMessage) {
      return checkoutError?.data?.klarna_redirect_url ? (
        <Alert>{checkoutError.detail}</Alert>
      ) : (
        <StepLayout.FetchErrorAlert error={checkoutError} fieldErrors={[checkoutErrors]} />
      );
    }
    if (klarnaSetupPaymentFailed) {
      return (
        <Alert variant="danger" shouldScrollOnMount={true}>
          Failed to checkout with Klarna. Please try again.{" "}
        </Alert>
      );
    }
    return <></>;
  };

  const resolveSubmitButtonText = () => {
    if (checkoutFlow === "klarna") {
      return "Place order with Klarna";
    }
    if (checkoutFlow === "wallet" && walletType) {
      return `Place order with ${walletDisplayText(walletType)}`;
    }
    return "Place order";
  };

  return (
    <StepLayout>
      <StepLayout.Section>
        <StepLayout.Header>
          <StepLayout.Title>Review Information</StepLayout.Title>
        </StepLayout.Header>
        <ReviewInfo clinic={clinic} client={client} cart={cart} />
      </StepLayout.Section>
      <div className={styles.priceSummaryContainer}>
        <StepLayout.Section>
          <StepLayout.Title>Review Order</StepLayout.Title>
          <CheckoutSummary cart={cart} size="s" tax={taxAmount} />
        </StepLayout.Section>
      </div>
      {renderCheckoutAlert()}
      {features.ZOETIS_PETCARE_REWARDS && <ZoetisPetcareRewardsSection />}
      {cart.is_autoship_confirmation_required && (
        <StepLayout.Section>
          <StepLayout.Header>
            <StepLayout.Title>Autoship Confirmation</StepLayout.Title>
            <StepLayout.Description>
              <div className={styles.autoshipConfirm}>
                {subscriptionCartItems.length > 0 ? (
                  <p>
                    Your card will be charged every time{" "}
                    {subscriptionCartItems.length > 1
                      ? "your items are dispensed for autoship."
                      : `${
                          subscriptionCartItems[0].item.product.product_line.name
                        } is dispensed for autoship (${subscriptionCartItems[0].subscription?.frequency_display.toLowerCase()}).`}{" "}
                    You can manage or cancel this at any time via the Autoships page.
                  </p>
                ) : null}
                <Checkbox
                  value={isAutoshipConfirmed}
                  onChange={setIsAutoshipConfirmed}
                  labelContent="I understand I will be charged automatically"
                  styles={{ label: styles.checkbox }}
                />
              </div>
            </StepLayout.Description>
          </StepLayout.Header>
        </StepLayout.Section>
      )}
      <StepLayout.Section>
        <StepLayout.Header>
          <StepLayout.Title>Payment Method</StepLayout.Title>
          <StepLayout.Description>
            Your credit card statement will reflect a charge from Vetcove.
            {cart.items.some((i) => i.is_approval_required) && (
              <span>
                {" "}
                Since your cart contains a prescription item, your veterinarian will be notified and
                will review your request. Your order will not be finalized, and your card will not
                be charged, until your veterinarian has approved your request.
              </span>
            )}
          </StepLayout.Description>
        </StepLayout.Header>
        {formError && <StepLayout.FetchErrorAlert error={formError} />}
        <PaymentMethodSelectPanel
          initialValue={cart.payment_method}
          value={selectedPaymentOption()}
          options={methods}
          isLoading={isWalletPaymentRequestLoading}
          walletPayment={supportedWallets}
          klarnaPaymentOption={cart.is_klarna_eligible ? klarnaPaymentOption : undefined}
          onChange={handlePaymentMethodChange}
          onAdd={() => openPaymentCreateDrawer({ onSuccess: handlePaymentMethodChange })}
        />
      </StepLayout.Section>
      {taxError && <StepLayout.FetchErrorAlert error={taxError} />}
      <StepLayout.Section>
        <div className={styles.section}>
          <StepLayout.Actions>
            <StepLayout.ActionButton.Previous onClick={onPrevious}>
              Return to Shipping
            </StepLayout.ActionButton.Previous>
            <StepLayout.ActionButton.Submit
              onClick={handleSubmit}
              disabled={isCheckoutDisabled || !!taxError}
              disabledHelp={getCheckoutDisabledHelp()}
              isLoading={isSubmitting || isLoading}
            >
              {resolveSubmitButtonText()}
            </StepLayout.ActionButton.Submit>
          </StepLayout.Actions>
          <span className={styles.acknowledgement}>
            By placing your order, you acknowledge and agree to our{" "}
            <TextButton
              className={styles.link}
              hasUnderline={true}
              dest={urls.support.privacyPolicy()}
            >
              Privacy Policy
            </TextButton>{" "}
            and{" "}
            <TextButton
              className={styles.link}
              hasUnderline={true}
              dest={urls.support.termsOfUse()}
            >
              Terms of Use
            </TextButton>
          </span>
        </div>
      </StepLayout.Section>
      <KlarnaCheckoutDialog isVisible={hasKlarnaDialog} />
      {walletType && <WalletCheckoutDialog isVisible={hasWalletDialog} wallet={walletType} />}
    </StepLayout>
  );
};

export default PaymentMethodCheckoutStep;
